import React from 'react';
import { RECORDING_LIVE_ONLY_COPY } from '../utils/onlineEventWording';

type Props = {
    recorded: boolean;
    variant: 'tile' | 'chip';
    /** Nur für Externe (Landing/Mail): Hinweis „nur Live, keine Nachreichung“. Intern nie. */
    showLiveOnlyHint?: boolean;
};

const RecOnIcon = ({ size = 18 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
        <rect x="2" y="6" width="14" height="12" rx="2" />
        <circle cx="9" cy="12" r="2" fill="currentColor" />
    </svg>
);

const RecOffIcon = ({ size = 18 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 2l20 20" />
        <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
        <path d="M16 16V6a2 2 0 0 0-2-2H6" />
        <path d="M2 8v10a2 2 0 0 0 2 2h10" />
    </svg>
);

export const RecordingLiveOnlyNotice: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`rounded-xl border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-4 ${className}`}>
        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-1">Bitte beachten</p>
        <p className="text-sm text-pe-text dark:text-slate-200 leading-relaxed">{RECORDING_LIVE_ONLY_COPY}</p>
    </div>
);

const EventRecordingBadge: React.FC<Props> = ({ recorded, variant, showLiveOnlyHint = false }) => {
    const label = recorded ? 'Wird aufgezeichnet' : 'Wird nicht aufgezeichnet';
    const chipLabel = recorded && showLiveOnlyHint ? 'Aufzeichnung · nur Live' : label;

    if (variant === 'chip') {
        return (
            <span
                className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${
                    recorded
                        ? 'bg-[#E6C299]/15 text-[#E6C299] border border-[#E6C299]/40'
                        : 'bg-slate-800 text-slate-300 border border-slate-600'
                }`}
            >
                {recorded ? <RecOnIcon size={11} /> : <RecOffIcon size={11} />}
                {chipLabel}
            </span>
        );
    }

    return (
        <div
            className={`flex items-center gap-3 p-3 rounded-xl border min-h-[4.5rem] ${
                recorded
                    ? 'border-[#E6C299]/50 bg-[#E6C299]/10'
                    : 'border-slate-700 bg-[#15171e]'
            }`}
        >
            <div
                className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 border ${
                    recorded
                        ? 'border-[#E6C299]/50 text-[#E6C299] bg-[#08090b]'
                        : 'border-slate-600 text-slate-300 bg-[#08090b]'
                }`}
            >
                {recorded ? <RecOnIcon size={20} /> : <RecOffIcon size={20} />}
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Aufzeichnung</p>
                <p className={`text-sm font-black leading-snug mt-0.5 ${recorded ? 'text-[#E6C299]' : 'text-white'}`}>
                    {label}
                </p>
                {recorded && showLiveOnlyHint && (
                    <p className="text-[10px] text-slate-400 leading-snug mt-1">
                        Nur Live-Teilnahme · keine Nachreichung
                    </p>
                )}
            </div>
        </div>
    );
};

export default EventRecordingBadge;
